import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  PlusCircle,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  MapPin,
  Building2,
  Hash,
  User,
  LayoutGrid,
  Rows3,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import DataTable from '../../components/DataTable';
import DynamicFormModal from '../../components/DynamicFormModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../components/ui/toaster.jsx';
import {
  fetchClients,
  createClient,
  updateClient,
  deleteClient,
} from './Services/clientsService';
import { clientFormFields } from './utils/clientsColumns';
import { cn } from '../../lib/utils';

const ESTADO_BADGE = {
  Activo: 'success',
  Inactivo: 'secondary',
};

const VIEW_STORAGE_KEY = 'clients:viewMode';

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function readStoredView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === 'table' ? 'table' : 'cards';
  } catch {
    return 'cards';
  }
}

export default function ClientsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 250);
  const [viewMode, setViewMode] = useState(readStoredView);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchClients({ query: { q: debouncedSearch } });
      setData(r?.data || []);
    } catch (e) {
      toast.error('Error al cargar clientes', e?.message || 'Intenta nuevamente');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = async (formData) => {
    setSubmitting(true);
    try {
      const r = editing
        ? await updateClient(editing._id, formData)
        : await createClient(formData);
      if (!r?.success) {
        toast.error(editing ? 'Error al actualizar' : 'Error al crear', r?.message);
        return;
      }
      toast.success(
        editing ? 'Cliente actualizado' : 'Cliente creado',
        editing ? formData.nombre : `${formData.nombre} (${formData.nit})`,
      );
      closeModal();
      load();
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const askDelete = (c) => {
    setDeleteId(c._id);
    setDeleteName(c.nombre);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const r = await deleteClient(deleteId);
      if (r?.success) {
        toast.success('Cliente eliminado', deleteName);
      } else {
        toast.error('Error al eliminar', r?.message);
        return;
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setDeleteId(null);
      setDeleteName('');
      load();
    }
  };

  const totalActivos = useMemo(
    () => data.filter((c) => c.estado === 'Activo').length,
    [data],
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 p-3 rounded-lg shrink-0">
                <Users className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Mis Clientes</h1>
                <p className="text-sm text-muted-foreground">
                  {data.length === 0
                    ? '0 clientes registrados'
                    : `${data.length} cliente${data.length === 1 ? '' : 's'} registrado${
                        data.length === 1 ? '' : 's'
                      } · ${totalActivos} activo${totalActivos === 1 ? '' : 's'}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <ViewToggle value={viewMode} onChange={setViewMode} />
              <Button variant="outline" onClick={load} disabled={loading}>
                <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                Actualizar
              </Button>
              <Button onClick={openCreate}>
                <PlusCircle className="h-4 w-4" />
                Nuevo Cliente
              </Button>
            </div>
          </div>

          <div className="mt-4 max-w-md">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, NIT o correo…"
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && data.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Cargando…
          </CardContent>
        </Card>
      )}

      {!loading && data.length === 0 && (
        <EmptyState hasSearch={Boolean(debouncedSearch)} onCreate={openCreate} />
      )}

      {data.length > 0 && viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data.map((c) => (
            <ClientCard
              key={c._id}
              client={c}
              onView={() => navigate(`/clients/${c._id}`)}
              onEdit={() => openEdit(c)}
              onDelete={() => askDelete(c)}
            />
          ))}
        </div>
      )}

      {data.length > 0 && viewMode === 'table' && (
        <DataTable
          data={data}
          columns={getTableColumns()}
          isLoading={loading}
          onAction={(action, row) => {
            if (action === 'view') return navigate(`/clients/${row._id}`);
            if (action === 'edit') return openEdit(row);
            if (action === 'delete') return askDelete(row);
          }}
          emptyMessage="Sin clientes"
        />
      )}

      <DynamicFormModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        fields={clientFormFields}
        item={editing}
        title={editing ? 'Editar Cliente' : 'Nuevo Cliente'}
        subtitle={
          editing
            ? 'Modifica los datos del cliente.'
            : 'Completa los datos básicos para registrar el cliente.'
        }
        submitLabel={editing ? 'Guardar cambios' : 'Crear Cliente'}
        twoColumns
        maxWidth="max-w-2xl"
      />

      <AlertDialog open={Boolean(deleteId)} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará a <strong>{deleteName}</strong>. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ViewToggle({ value, onChange }) {
  const options = [
    { v: 'cards', label: 'Tarjetas', icon: LayoutGrid },
    { v: 'table', label: 'Tabla', icon: Rows3 },
  ];
  return (
    <div className="inline-flex rounded-md border border-border bg-card p-0.5 shadow-sm">
      {options.map((o) => {
        const Icon = o.icon;
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            title={o.label}
            aria-label={o.label}
            aria-pressed={active}
            className={cn(
              'inline-flex items-center justify-center h-9 w-9 rounded transition-colors',
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

function getTableColumns() {
  return [
    {
      id: 'nombre',
      label: 'Cliente',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <Building2 className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0">
            <div className="font-medium text-foreground truncate max-w-[200px]">
              {row.nombre}
            </div>
            {row.nombreContacto && (
              <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                {row.nombreContacto}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'nit',
      label: 'NIT',
      sortable: true,
      width: '130px',
      render: (row) => <span className="font-mono text-xs">{row.nit}</span>,
    },
    {
      id: 'correo',
      label: 'Correo',
      render: (row) => (
        <a
          href={`mailto:${row.correo}`}
          className="text-sm hover:text-foreground text-muted-foreground"
        >
          {row.correo}
        </a>
      ),
    },
    {
      id: 'telefono',
      label: 'Teléfono',
      width: '150px',
      render: (row) =>
        row.telefono ? (
          <span className="text-sm text-muted-foreground">{row.telefono}</span>
        ) : (
          <span className="text-muted-foreground italic text-xs">—</span>
        ),
    },
    {
      id: 'tipoEmpresa',
      label: 'Tipo',
      width: '110px',
      render: (row) =>
        row.tipoEmpresa ? (
          <Badge variant="outline" className="font-normal">
            {row.tipoEmpresa}
          </Badge>
        ) : (
          <span className="text-muted-foreground italic text-xs">—</span>
        ),
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'status',
      sortable: true,
      width: '110px',
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        { key: 'view', icon: 'Eye', tooltip: 'Ver detalle' },
        { key: 'edit', icon: 'Edit', tooltip: 'Editar' },
        {
          key: 'delete',
          icon: 'Trash',
          tooltip: 'Eliminar',
          className: 'hover:bg-destructive hover:text-destructive-foreground',
        },
      ],
    },
  ];
}

function EmptyState({ hasSearch, onCreate }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card>
        <CardContent className="py-16 px-6">
          <div className="flex flex-col items-center text-center max-w-md mx-auto space-y-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Users className="h-8 w-8" />
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-semibold text-foreground">
                {hasSearch ? 'Sin resultados' : 'Sin clientes todavía'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {hasSearch
                  ? 'No hay clientes que coincidan con tu búsqueda. Prueba con otro término.'
                  : 'Crea tu primer cliente para comenzar a gestionar sus documentos y embarques.'}
              </p>
            </div>
            {!hasSearch && (
              <Button onClick={onCreate}>
                <PlusCircle className="h-4 w-4" />
                Crear primer cliente
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function ClientCard({ client, onView, onEdit, onDelete }) {
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn?.();
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      whileHover={{ y: -2 }}
    >
      <Card
        className="h-full cursor-pointer transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring"
        onClick={onView}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onView?.();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={`Ver detalle de ${client.nombre}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center text-primary shrink-0">
              <Building2 className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground truncate" title={client.nombre}>
                  {client.nombre}
                </h3>
                <Badge
                  variant={ESTADO_BADGE[client.estado] || 'secondary'}
                  className="shrink-0"
                >
                  {client.estado}
                </Badge>
              </div>
              {client.tipoEmpresa && (
                <p className="text-xs text-muted-foreground mt-0.5">{client.tipoEmpresa}</p>
              )}
            </div>
          </div>

          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 min-w-0">
              <Hash className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">NIT {client.nit}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <a
                href={`mailto:${client.correo}`}
                onClick={(e) => e.stopPropagation()}
                className="truncate hover:text-foreground"
                title={client.correo}
              >
                {client.correo}
              </a>
            </div>
            {client.telefono && (
              <div className="flex items-center gap-2 min-w-0">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{client.telefono}</span>
              </div>
            )}
            {client.nombreContacto && (
              <div className="flex items-center gap-2 min-w-0">
                <User className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{client.nombreContacto}</span>
              </div>
            )}
            {client.direccion && (
              <div className="flex items-center gap-2 min-w-0">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate" title={client.direccion}>
                  {client.direccion}
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={stop(onView)}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Eye className="h-3.5 w-3.5" />
              Ver
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={stop(onEdit)}
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <Edit className="h-3.5 w-3.5" />
              Editar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={stop(onDelete)}
              aria-label="Eliminar"
              className="h-8 px-2 text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
